// Reading a lead sheet's PERFORMANCE ROLES: which written bars are the pickup,
// which are the repeatable chorus, and which are the terminal coda.
//
// A lead sheet notates a piece; a backing track needs a performance. The sheet
// gives material + a map (repeats, segno, To Coda, D.S./D.C., Fine) — enough to
// derive exactly one literal reading, which `flattenMeasures` already computes.
// It does NOT encode the jazz itinerary (head → solos → head out, melody only
// on the head passes, coda only on the last one): that is convention, and it
// belongs in an explicit arrangement policy layered on top of this analysis.
//
// This module does only the derivable half. Everything it cannot read from the
// sheet it either leaves null or records in `inferences`, so a caller (and the
// UI) can tell what the chart actually said from what we guessed.

import type { IRealChart, IRealMeasure } from './ireal/types';

/** An inclusive range of WRITTEN measure indices. */
export interface FormSpan {
  start: number;
  end: number;
}

/** A direction to solo over existing music, rather than a separately written solo part. */
export interface SoloReference extends FormSpan {
  section: string;
  takeRepeat: boolean;
}

/** How confidently the structure was read, and from what. */
export type FormKind =
  | 'segno-coda' // segno + coda marks + a D.S./D.C.: a full navigation map
  | 'coda' // a coda section, but no segno to return to
  | 'repeat' // repeat barlines only — the chorus is the repeated span
  | 'plain'; // no structure at all: the whole sheet is the chorus

export interface FormAnalysis {
  /** Leading anacrusis, when the chart opens with one (never part of a chorus). */
  pickup: FormSpan | null;
  /** A leading intro region: played once at the top, never restated. */
  intro: FormSpan | null;
  /**
   * A WRITTEN solo region ("Solos x4" staff text, a section named Solo, or
   * an explicit sectionRole) — the chart wrote its itinerary down: solos
   * play here, not over the head form.
   */
  solos: FormSpan | null;
  /** "Solos on B" reuses a named section of the head. */
  soloReference: SoloReference | null;
  /** Navigation explicitly deferred until after the solos (written bar index). */
  afterSolos: number | null;
  /** The repeatable unit — one head, or one solo chorus. */
  chorus: FormSpan;
  /** Terminal material, played only on the final pass. */
  coda: FormSpan | null;
  /**
   * "To Coda": the sign sits at the START of this written bar, so on the
   * final pass the chorus leaves WITHOUT playing it and jumps to the coda —
   * the coda replaces the core's tail (iReal: "plays until the first coda
   * symbol"). Null means play the whole chorus, then append the coda.
   */
  codaExit: number | null;
  kind: FormKind;
  /** Anything inferred rather than read — surfaced so the UI can say so. */
  inferences: string[];
}

const NAV_RE = /D\.\s*[SC]\./i;
const FINE_RE = /fine/i;

function readSoloReference(measures: IRealMeasure[], inferences: string[]): SoloReference | null {
  for (let at = 0; at < measures.length; at += 1) {
    const text = `${measures[at].staffText ?? ''} ${measures[at].directive ?? ''}`;
    const ref = /\bsolos?\s+on\s+(?:section\s+)?([a-z][a-z0-9]*)(?=\s|[.:;,]|$)/i.exec(text);
    if (!ref) continue;
    const label = ref[1].toUpperCase();
    const matches = (name: string) => measures.flatMap((m, i) =>
      i <= at && m.section?.trim().toUpperCase() === name ? [i] : [],
    );
    const exact = matches(label);
    // "CD" may name consecutive C and D sections, but never invent a form
    // from ambiguous/repeated letters such as AABA.
    const labels = exact.length ? [label] : [...label];
    const targets = labels.map(matches);
    const starts = targets.map((indices) => indices[0]);
    const ordered = starts.every((start, i) => i === 0 || start > starts[i - 1]);
    const contiguous = labels.length === 1 || measures.slice(starts[0], starts.at(-1)! + 1)
      .filter((m) => m.section).map((m) => m.section!.trim().toUpperCase()).join('') === label;
    if (targets.some((indices) => indices.length !== 1) || !ordered || !contiguous) {
      inferences.push(`Solo instruction "${ref[0]}" has no unambiguous section target.`);
      continue;
    }
    const start = starts[0];
    const lastSection = starts[starts.length - 1];
    const takeRepeat = /^\s*[:;,.([]?\s*take\s+(?:the\s+)?repeat\b/i.test(text.slice(ref.index + ref[0].length));
    let end = at;
    for (let i = lastSection + 1; i <= at; i += 1) {
      if (measures[i].section || measures[i].sectionRole || measures[i].coda) {
        end = i - 1;
        break;
      }
    }
    if (takeRepeat) {
      // Keep the repeat-side ending for every solo. The player's chorus
      // count, rather than the written second ending, exits this loop.
      if (measures[start].open !== 'repeat') {
        inferences.push(`Solo instruction "${ref[0]}" asks for a repeat without a matching opening.`);
        continue;
      }
      let depth = 0;
      let close = -1;
      for (let i = start; i <= at; i += 1) {
        if (measures[i].open === 'repeat') depth += 1;
        if (measures[i].close === 'repeat' && --depth === 0) {
          close = i;
          break;
        }
      }
      if (close < 0) {
        inferences.push(`Solo instruction "${ref[0]}" has no matching closing repeat.`);
        continue;
      }
      end = close;
    }
    if (end >= start) return { start, end, section: label, takeRepeat };
  }
  return null;
}

function lastIndex(
  measures: IRealMeasure[],
  test: (m: IRealMeasure, i: number) => boolean,
): number {
  for (let i = measures.length - 1; i >= 0; i -= 1) if (test(measures[i], i)) return i;
  return -1;
}

/**
 * Decompose a chart into pickup / chorus / coda. Pure and total: every chart
 * yields a chorus, even an empty or structureless one, so callers never have to
 * handle a null analysis.
 */
export function analyzeForm(chart: IRealChart): FormAnalysis {
  const measures = chart.measures;
  const inferences: string[] = [];
  const lastBar = Math.max(0, measures.length - 1);

  // A pickup is the opening bar only: a short first measure IS the anacrusis
  // convention, and multi-bar intros are written as sections, not pickups.
  const pickup: FormSpan | null =
    measures[0]?.pickupBeats != null ? { start: 0, end: 0 } : null;

  const segno = measures.findIndex((m) => m.segno);
  const codaMarks = measures.reduce<number[]>((out, m, i) => (m.coda ? [...out, i] : out), []);
  const navBar = measures.findIndex((m) => NAV_RE.test(m.directive ?? ''));
  const fineBar = measures.findIndex((m) => FINE_RE.test(m.directive ?? ''));
  const afterSolosIndex = measures.findIndex((m) =>
    NAV_RE.test(m.directive ?? '') &&
    /\bafter\s+(?:the\s+)?solos?\b/i.test(`${m.staffText ?? ''} ${m.directive ?? ''}`),
  );
  const afterSolos = afterSolosIndex < 0 ? null : afterSolosIndex;

  // --- role regions: explicit marks win, then conservative text evidence ---
  // A region runs from its marked bar to the next section/role/coda mark.
  const regionEnd = (start: number, stops: (m: IRealMeasure) => boolean): number => {
    for (let i = start + 1; i < measures.length; i += 1) {
      if (stops(measures[i])) return i - 1;
    }
    return lastBar;
  };
  // A written solo form runs to the next coda sign or explicit role — NOT to
  // the next section letter (Moanin' writes its solo AABA out in full, [B]
  // and all, with its own segno/Fine navigation).
  const solosStops = (m: IRealMeasure) => !!m.sectionRole || !!m.coda;
  const introStops = (m: IRealMeasure) => !!m.section || !!m.sectionRole;
  const firstBody = pickup ? 1 : 0;

  let solos: FormSpan | null = null;
  const explicitSolos = measures.findIndex((m) => m.sectionRole === 'solos');
  if (explicitSolos >= 0) {
    solos = { start: explicitSolos, end: regionEnd(explicitSolos, solosStops) };
  } else {
    // Staff text that BEGINS with "Solo(s)" prescribes the region ("Solos
    // x4", "Solos:"); a guide sentence merely mentioning solos ("Take coda
    // 2nd time after solos") does not.
    const textBar = measures.findIndex(
      (m) => (
        /^\(?solos?\b/i.test(m.staffText ?? '') &&
        (!/^\(?solos?\s+on\b/i.test(m.staffText ?? '') || m.open === 'repeat')
      ) || /^\(?solos?\)?$/i.test(m.section ?? ''),
    );
    if (textBar > firstBody) {
      const end = regionEnd(textBar, solosStops);
      // A WRITTEN solo form is a playable region: at least a chorus-sized
      // span, or a self-contained repeated vamp (Un Poco Loco's 4-bar
      // |: :| 8x — the region must OPEN its own repeat; ending inside some
      // larger repeat's tail doesn't count). A short unrepeated tail
      // ("solos on AABA" over two turnaround bars) is an INSTRUCTION —
      // solo over the head form, the default itinerary.
      const isForm = end - textBar + 1 >= 8 || measures[textBar].open === 'repeat';
      if (isForm) {
        solos = { start: textBar, end };
        inferences.push(
          `Solo section read from the "${measures[textBar].staffText ?? measures[textBar].section}" marking.`,
        );
      }
    }
  }

  // A separately written solo region already owns its route, including
  // internal repeats. A summary such as "Solo on CDCDEF" need not fit the
  // simpler contiguous-section reference model used by unwritten solos.
  const soloReference = solos ? null : readSoloReference(measures, inferences);

  let intro: FormSpan | null = null;
  const explicitIntro = measures.findIndex((m) => m.sectionRole === 'intro');
  // The section governing the first body bar: a mark written on the pickup
  // bar names the music it leads into (Chameleon's boxed "intro" sits on its
  // pickup measure). Verses ride the same rung — iReal's `*v` (A Foggy Day,
  // Lush Life) is also lead-in material a band states once, never loops.
  const leadName =
    measures[firstBody]?.section ?? (firstBody > 0 ? measures[0]?.section : undefined);
  if (explicitIntro >= 0) {
    intro = { start: explicitIntro, end: regionEnd(explicitIntro, introStops) };
  } else if (leadName && /^(i|in|intro|v|verse)$/i.test(leadName)) {
    const end = regionEnd(firstBody, introStops);
    // Only an intro when head material remains after it — a chart whose only
    // non-solo section is named "i" is USING it as the head (Goodbye Pork
    // Pie Hat's theme lives in its [i] section).
    if (end + 1 < (solos?.start ?? lastBar + 1) && end < lastBar) {
      intro = { start: firstBody, end };
    }
  } else {
    // Unmarked leading material before the first |: is how engraved corpora
    // write intros when they mark nothing else (Nica's Dream: 8 bars closed
    // by a double bar, then the A section's repeat). Conservative, and an
    // INFERENCE the UI can report: nothing structural inside the lead, a
    // modest length (the realbook leads cluster at 4/8/16 bars; Blood
    // Count's 38-bar tail-repeat shape must not read as an intro), and a
    // real form remaining after it.
    const firstOpen = measures.findIndex((m) => m.open === 'repeat');
    const leadBars = firstOpen - firstBody;
    const leadMarked = measures
      .slice(firstBody, Math.max(firstBody, firstOpen))
      .some(
        (m) =>
          m.section != null ||
          m.sectionRole != null ||
          m.segno ||
          m.coda ||
          m.ending != null ||
          m.directive != null,
      );
    if (
      firstOpen > firstBody &&
      leadBars <= 16 &&
      !leadMarked &&
      lastBar + 1 - firstOpen >= 8 &&
      firstOpen < (solos?.start ?? lastBar + 1)
    ) {
      intro = { start: firstBody, end: firstOpen - 1 };
      inferences.push('Intro inferred: the unmarked bars before the first repeat play once.');
    }
  }

  const segnoEff = solos && segno >= solos.start && segno <= solos.end ? -1 : segno;
  // The chorus — the unit that loops — starts past the entry material: after
  // a segno when one is written, else after the intro/verse, else after the
  // pickup bar. (A degenerate intro reaching the last bar is ignored so the
  // chorus stays non-empty.)
  const chorusStart =
    segnoEff >= 0 ? segnoEff : intro && intro.end < lastBar ? intro.end + 1 : pickup ? 1 : 0;
  // A nav directive INSIDE the written solo region belongs to its play
  // instructions, not to the chorus (Goodbye Pork Pie Hat's D.C. closes its
  // Solos section).
  const navBarEff = solos && navBar >= solos.start && navBar <= solos.end ? -1 : navBar;

  // --- the coda, and where the chorus exits into it ---
  let coda: FormSpan | null = null;
  let codaExit: number | null = null;
  if (codaMarks.length >= 2) {
    // The conventional pair: the first mark is "To Coda", the last is the
    // coda section itself (the same reading flattenMeasures uses).
    codaExit = codaMarks[0];
    coda = { start: codaMarks[codaMarks.length - 1], end: lastBar };
  } else if (codaMarks.length === 1 && navBar >= 0 && navBar < lastBar) {
    // One mark plus a D.S./D.C. al Coda: the jump target was never written
    // (500 Miles High's source even says "fix the coda thing" and leaves the
    // second mark commented out). Everything after the navigation bar is
    // materially the coda — say so rather than silently playing it inline.
    codaExit = codaMarks[0];
    coda = { start: navBar + 1, end: lastBar };
    inferences.push('Coda target inferred: the bars after the D.S./D.C., which carry no Coda sign.');
  } else if (codaMarks.length === 1) {
    // A lone mark with nothing to jump from simply labels the tail.
    coda = { start: codaMarks[0], end: lastBar };
  }
  const explicitCoda = measures.findIndex((m) => m.sectionRole === 'coda');
  if (!coda && explicitCoda >= 0)
    coda = { start: explicitCoda, end: regionEnd(explicitCoda, introStops) };
  // A trailing section NAMED coda/outro/tag/end is terminal material the same
  // way (Chameleon's boxed "Coda" vamp, openbook's "End" bars) — but only
  // when it really is the tail: a coda-named section with music after it is a
  // coda TARGET, which the ⊕-mark machinery owns.
  if (!coda) {
    const namedCoda = measures.findIndex(
      (m, i) =>
        i > chorusStart && m.section != null && /^(coda|outro|tag|end|ending)\.?$/i.test(m.section),
    );
    if (namedCoda >= 0 && regionEnd(namedCoda, introStops) === lastBar) {
      coda = { start: namedCoda, end: lastBar };
    }
  }

  // --- where the chorus ends ---
  /** The chorus end implied by the structure, optionally ignoring the coda. */
  const endOfChorus = (withCoda: FormSpan | null): number => {
    const bound = (v: number) => (solos && v >= solos.start ? solos.start - 1 : v);
    if (fineBar >= chorusStart) return bound(fineBar); // al Fine stops the chorus there.
    if (navBarEff >= chorusStart) return bound(navBarEff); // the D.S./D.C. bar is played each pass.
    if (withCoda && withCoda.start > chorusStart) return bound(withCoda.start - 1);
    const closeRepeat = lastIndex(
      measures,
      (m, i) => (m.close === 'repeat' || m.close === 'final') && (!solos || i < solos.start),
    );
    return closeRepeat >= chorusStart ? closeRepeat : bound(lastBar);
  };

  let chorusEnd = Math.max(chorusStart, Math.min(endOfChorus(coda), lastBar));

  // A one-bar chorus is not a form: the coda marks were misread (or the chart
  // is degenerate). Drop the coda reading — and only ITS inference; an intro
  // read independently above stays reported — and take the plain span.
  if (coda && chorusEnd - chorusStart + 1 < 2) {
    coda = null;
    codaExit = null;
    const kept = inferences.filter((line) => !line.startsWith('Coda target inferred'));
    inferences.length = 0;
    inferences.push(...kept);
    chorusEnd = Math.max(chorusStart, Math.min(endOfChorus(null), lastBar));
  }

  // A To Coda outside the chorus can't be an exit from it. With a WRITTEN
  // solo region the whole pre-solos span is the head (The Intrepid Fox's ⊕
  // sits past a stray "Fine" that caps the chorus), so validate against it.
  const exitLo = solos ? firstBody : chorusStart;
  const exitHi = solos ? solos.start - 1 : chorusEnd;
  if (codaExit !== null && (codaExit < exitLo || codaExit > exitHi)) codaExit = null;

  // The coda never swallows the written solo region: a target BEFORE the
  // solos clips to it (Un Poco Loco's ⊕ tail sits between the D.S. and its
  // closing solo vamp); a mark inside the region is the region's own.
  if (coda && solos) {
    if (coda.start >= solos.start && coda.start <= solos.end) coda = null;
    else if (coda.start < solos.start) coda = { start: coda.start, end: Math.min(coda.end, solos.start - 1) };
  }

  const kind: FormKind =
    coda && segno >= 0
      ? 'segno-coda'
      : coda
        ? 'coda'
        : measures.some((m) => m.open === 'repeat' || m.close === 'repeat')
          ? 'repeat'
          : 'plain';

  if (segno < 0 && coda) {
    inferences.push('No segno: the chorus is taken from the top of the form.');
  }

  return { pickup, intro, solos, soloReference, afterSolos, chorus: { start: chorusStart, end: chorusEnd }, coda, codaExit, kind, inferences };
}

/** Bars in a span (inclusive). */
export function spanLength(span: FormSpan): number {
  return span.end - span.start + 1;
}
